import React, { useEffect, useState } from 'react';
import PersonGroupForm from './PersonGroupForm';
import '../styles/TaskList.css';
import useApiStable from '../utils/useApiStable';

function PersonGroupList() {
    const [groups, setGroups] = useState([]);
    const [persons, setPersons] = useState([]);
    const [groupMembers, setGroupMembers] = useState({}); // Map: groupId -> members[]
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [error, setError] = useState('');
    const api = useApiStable();

    const loadGroups = () => {
        setError('');
        api.get('/person-groups')
            .then(data => setGroups(Array.isArray(data) ? data : []))
            .catch(err => setError(err.message || 'Fehler beim Laden der Gruppen'));
    };

    const loadPersons = () => {
        api.get('/persons')
            .then(data => setPersons(Array.isArray(data) ? data : []))
            .catch(err => console.error('Fehler beim Laden der Personen:', err));
    };

    const loadGroupMembers = (groupId) => {
        if (groupMembers[groupId]) {
            // Bereits geladen
            return;
        }

        api.get(`/person-groups/${groupId}/members`)
            .then(data => {
                setGroupMembers(prev => ({
                    ...prev,
                    [groupId]: Array.isArray(data) ? data : []
                }));
            })
            .catch(err => console.error('Fehler beim Laden der Gruppenmitglieder:', err));
    };

    useEffect(() => {
        loadGroups();
        loadPersons();
    }, []);

    const handleEdit = (group) => {
        // Lade die Mitglieder der Gruppe vor dem Bearbeiten
        api.get(`/person-groups/${group.id}/members`)
            .then(members => {
                const groupWithMembers = {
                    ...group,
                    members: members
                };
                setSelectedGroup(groupWithMembers);
                setShowForm(true);
            })
            .catch(err => {
                console.error('Fehler beim Laden der Gruppenmitglieder:', err);
                // Öffne das Formular trotzdem, aber ohne Mitglieder
                setSelectedGroup(group);
                setShowForm(true);
            });
    };

    const handleCreate = () => {
        setSelectedGroup(null);
        setShowForm(true);
    };

    const handleDelete = (group) => {
        if (window.confirm(`Gruppe "${group.name}" wirklich löschen?`)) {
            api.delete(`/person-groups/${group.id}`)
                .then(() => loadGroups())
                .catch(err => setError(err.message || 'Fehler beim Löschen'));
        }
    };

    const handleFormClose = (refresh = false) => {
        setShowForm(false);
        setSelectedGroup(null);
        if (refresh) {
            loadGroups();
            // Cache für Gruppenmitglieder leeren, damit sie neu geladen werden
            setGroupMembers({});
        }
    };

    const toggleExpand = (groupId) => {
        if (expandedGroupId === groupId) {
            setExpandedGroupId(null);
        } else {
            setExpandedGroupId(groupId);
            loadGroupMembers(groupId);
        }
    };

    return (
        <div className="tasklist-container">
            <div className="tasklist-header">
                <h2>Personengruppen</h2>
                <button className="tasklist-btn" onClick={handleCreate}>Neue Gruppe</button>
            </div>
            {error && (
                <div style={{
                    color: '#c62828',
                    fontSize: '0.85em',
                    marginBottom: '8px'
                }}>
                    {error}
                </div>
            )}

                <ul className="tasklist-list">
                    {groups.map(group => (
                        <li className="tasklist-item" key={group.id}>
                            <div 
                                style={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    width: '100%' 
                                }}
                            >
                                <div className="tasklist-item-info">
                                    <strong>{group.name}</strong>
                                    <span style={{ fontSize: '0.9em', color: '#666', marginLeft: '8px' }}>
                                        ({group.memberCount || 0} Mitglied{group.memberCount !== 1 ? 'er' : ''})
                                    </span>
                                    {group.description && (
                                        <div style={{ 
                                            fontSize: '0.85em', 
                                            color: '#888', 
                                            marginTop: '4px' 
                                        }}>
                                            {group.description}
                                        </div>
                                    )}
                                </div>
                                
                                {expandedGroupId === group.id && (
                                    <div style={{ 
                                        marginTop: '12px', 
                                        paddingTop: '12px', 
                                        borderTop: '1px solid #eee' 
                                    }}>
                                        <strong style={{ fontSize: '0.9em' }}>Mitglieder:</strong>
                                        {!groupMembers[group.id] ? (
                                            <p style={{
                                                fontSize: '0.85em',
                                                color: '#999',
                                                marginTop: '4px'
                                            }}>
                                                Laden...
                                            </p>
                                        ) : groupMembers[group.id].length === 0 ? (
                                            <p style={{
                                                fontSize: '0.85em', 
                                                color: '#999', 
                                                marginTop: '4px' 
                                            }}>
                                                Keine Mitglieder
                                            </p>
                                        ) : (
                                            <ul style={{ 
                                                marginTop: '8px', 
                                                paddingLeft: '20px' 
                                            }}>
                                                {groupMembers[group.id].map(member => (
                                                    <li
                                                        key={member.id} 
                                                        style={{ 
                                                            fontSize: '0.85em', 
                                                            marginBottom: '4px' 
                                                        }}
                                                    >
                                                        {member.name} ({member.email})
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button 
                                    className="tasklist-edit-btn" 
                                    onClick={() => toggleExpand(group.id)}
                                    style={{ background: '#2196F3' }}
                                >
                                    {expandedGroupId === group.id ? 'Verbergen' : 'Details'}
                                </button>
                                <button 
                                    className="tasklist-edit-btn" 
                                    onClick={() => handleEdit(group)}
                                >
                                    Bearbeiten
                                </button>
                                <button 
                                    className="tasklist-edit-btn" 
                                    style={{ background: '#aaa' }}
                                    onClick={() => handleDelete(group)}
                                >
                                    Löschen
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>

            {showForm && (
                <PersonGroupForm 
                    group={selectedGroup} 
                    persons={persons}
                    onClose={handleFormClose}
                />
            )}
        </div>
    );
}

export default PersonGroupList;
